/**
 * Guest Checkout Modal Component
 * 
 * Modal for collecting guest user information before checkout
 */

import { useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';

const GuestCheckoutModal = ({ isOpen, onClose, onSubmit, _localization }) => {
    const [formData, setFormData] = useState({
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        company: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        postcode: '',
        country: 'TW',
        notes: ''
    });
    
    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    /**
     * Handle form input changes
     */
    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear error when user starts typing
        if (formErrors[field]) {
            setFormErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };
    
    /**
     * Validate form data
     */
    const validateForm = () => {
        const errors = {};
        
        // Required fields
        if (!formData.email.trim()) {
            errors.email = __('電子郵件為必填項目', 'frontend-order-system');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = __('請輸入有效的電子郵件地址', 'frontend-order-system');
        }
        
        if (!formData.firstName.trim()) {
            errors.firstName = __('名字為必填項目', 'frontend-order-system');
        }
        
        if (!formData.lastName.trim()) {
            errors.lastName = __('姓氏為必填項目', 'frontend-order-system');
        }
        
        if (!formData.phone.trim()) {
            errors.phone = __('電話號碼為必填項目', 'frontend-order-system');
        }
        
        if (!formData.address1.trim()) {
            errors.address1 = __('地址為必填項目', 'frontend-order-system');
        }
        
        if (!formData.city.trim()) {
            errors.city = __('城市為必填項目', 'frontend-order-system');
        }
        
        if (!formData.postcode.trim()) {
            errors.postcode = __('郵遞區號為必填項目', 'frontend-order-system');
        }
        
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };
    
    /**
     * Handle form submission
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setIsSubmitting(true);
        
        try {
            // Submit guest information
            await onSubmit(formData);
        } catch (error) {
            setFormErrors({
                submit: error.message || __('提交失敗，請重試', 'frontend-order-system')
            });
        } finally {
            setIsSubmitting(false);
        }
    };
    
    /**
     * Render form field
     */
    const renderField = (field, label, type = 'text', required = true, placeholder = '') => (
        <div style={{ marginBottom: '16px' }}>
            <label style={{
                display: 'block',
                marginBottom: '4px',
                fontSize: '0.9rem',
                fontWeight: '500',
                color: '#333'
            }}>
                {label}
                {required && <span style={{ color: '#dc3545' }}> *</span>}
            </label>
            
            {type === 'textarea' ? (
                <textarea
                    value={formData[field]}
                    onChange={(e) => handleInputChange(field, e.target.value)}
                    placeholder={placeholder}
                    rows={3}
                    style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: `1px solid ${formErrors[field] ? '#dc3545' : '#ccc'}`,
                        borderRadius: '4px',
                        fontSize: '14px',
                        resize: 'vertical'
                    }}
                />
            ) : type === 'select' ? (
                <select
                    value={formData[field]}
                    onChange={(e) => handleInputChange(field, e.target.value)}
                    style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: `1px solid ${formErrors[field] ? '#dc3545' : '#ccc'}`,
                        borderRadius: '4px',
                        fontSize: '14px'
                    }}
                >
                    <option value="TW">{__('台灣', 'frontend-order-system')}</option>
                    <option value="CN">{__('中國', 'frontend-order-system')}</option>
                    <option value="HK">{__('香港', 'frontend-order-system')}</option>
                    <option value="MO">{__('澳門', 'frontend-order-system')}</option>
                    <option value="SG">{__('新加坡', 'frontend-order-system')}</option>
                    <option value="MY">{__('馬來西亞', 'frontend-order-system')}</option>
                </select>
            ) : (
                <input
                    type={type}
                    value={formData[field]}
                    onChange={(e) => handleInputChange(field, e.target.value)}
                    placeholder={placeholder}
                    style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: `1px solid ${formErrors[field] ? '#dc3545' : '#ccc'}`,
                        borderRadius: '4px',
                        fontSize: '14px'
                    }}
                />
            )}
            
            {formErrors[field] && (
                <div style={{
                    marginTop: '4px',
                    fontSize: '0.8rem',
                    color: '#dc3545'
                }}>
                    {formErrors[field]}
                </div>
            )}
        </div>
    );
    
    if (!isOpen) return null;
    
    return (
        <div className="fos-modal-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
        }}>
            <div className="fos-modal-content" style={{
                background: '#fff',
                borderRadius: '8px',
                width: '100%',
                maxWidth: '600px',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}>
                {/* Modal Header */}
                <div style={{
                    padding: '20px 20px 0',
                    borderBottom: '1px solid #e1e1e1',
                    marginBottom: '20px'
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <h3 style={{ margin: 0, color: '#333' }}>
                            👤 {__('訪客結帳信息', 'frontend-order-system')}
                        </h3>
                        
                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                background: 'none',
                                border: 'none',
                                fontSize: '24px',
                                cursor: 'pointer',
                                padding: '0',
                                color: '#666'
                            }}
                            aria-label={__('關閉', 'frontend-order-system')}
                        >
                            ×
                        </button>
                    </div>
                    
                    <p style={{
                        margin: '8px 0 0',
                        color: '#666',
                        fontSize: '0.9rem'
                    }}>
                        {__('請填寫以下信息以完成訂單', 'frontend-order-system')}
                    </p>
                </div>
                
                {/* Modal Body */}
                <div style={{ padding: '0 20px 20px' }}>
                    <form onSubmit={handleSubmit}>
                        {/* Contact Information */}
                        <div style={{ marginBottom: '24px' }}>
                            <h4 style={{
                                margin: '0 0 16px',
                                padding: '8px 0',
                                borderBottom: '1px solid #e1e1e1',
                                color: '#333'
                            }}>
                                📧 {__('聯絡信息', 'frontend-order-system')}
                            </h4>
                            
                            {renderField('email', __('電子郵件', 'frontend-order-system'), 'email', true, 'example@email.com')}
                            
                            <div style={{ display: 'flex', gap: '12px' }}>
                                <div style={{ flex: 1 }}>
                                    {renderField('firstName', __('名字', 'frontend-order-system'), 'text', true)}
                                </div>
                                <div style={{ flex: 1 }}>
                                    {renderField('lastName', __('姓氏', 'frontend-order-system'), 'text', true)}
                                </div>
                            </div>
                            
                            <div style={{ display: 'flex', gap: '12px' }}>
                                <div style={{ flex: 1 }}>
                                    {renderField('phone', __('電話號碼', 'frontend-order-system'), 'tel', true, '0912-345-678')}
                                </div>
                                <div style={{ flex: 1 }}>
                                    {renderField('company', __('公司名稱', 'frontend-order-system'), 'text', false)}
                                </div>
                            </div>
                        </div>
                        
                        {/* Shipping Address */}
                        <div style={{ marginBottom: '24px' }}>
                            <h4 style={{
                                margin: '0 0 16px',
                                padding: '8px 0',
                                borderBottom: '1px solid #e1e1e1',
                                color: '#333'
                            }}>
                                🏠 {__('收貨地址', 'frontend-order-system')}
                            </h4>
                            
                            {renderField('address1', __('地址', 'frontend-order-system'), 'text', true)}
                            {renderField('address2', __('地址 2 (選填)', 'frontend-order-system'), 'text', false)}
                            
                            <div style={{ display: 'flex', gap: '12px' }}>
                                <div style={{ flex: 1 }}>
                                    {renderField('city', __('城市', 'frontend-order-system'), 'text', true)}
                                </div>
                                <div style={{ flex: 1 }}>
                                    {renderField('state', __('州/省', 'frontend-order-system'), 'text', false)}
                                </div>
                            </div>
                            
                            <div style={{ display: 'flex', gap: '12px' }}>
                                <div style={{ flex: 1 }}>
                                    {renderField('postcode', __('郵遞區號', 'frontend-order-system'), 'text', true)}
                                </div>
                                <div style={{ flex: 1 }}>
                                    {renderField('country', __('國家/地區', 'frontend-order-system'), 'select', true)}
                                </div>
                            </div>
                        </div>
                        
                        {/* Order Notes */}
                        <div style={{ marginBottom: '24px' }}>
                            <h4 style={{
                                margin: '0 0 16px',
                                padding: '8px 0',
                                borderBottom: '1px solid #e1e1e1',
                                color: '#333'
                            }}>
                                📝 {__('訂單備註', 'frontend-order-system')}
                            </h4>
                            
                            {renderField('notes', __('備註 (選填)', 'frontend-order-system'), 'textarea', false, __('有任何特殊需求請在此說明...', 'frontend-order-system'))}
                        </div>
                        
                        {/* Submit Error */}
                        {formErrors.submit && (
                            <div style={{
                                marginBottom: '16px',
                                padding: '12px',
                                background: '#f8d7da',
                                color: '#721c24',
                                border: '1px solid #f5c6cb',
                                borderRadius: '4px',
                                fontSize: '0.9rem'
                            }}>
                                ⚠️ {formErrors.submit}
                            </div>
                        )}
                        
                        {/* Form Actions */}
                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            paddingTop: '16px',
                            borderTop: '1px solid #e1e1e1'
                        }}>
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={isSubmitting}
                                style={{
                                    flex: 1,
                                    padding: '12px 20px',
                                    background: '#f0f0f1',
                                    color: '#333',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    fontSize: '1rem',
                                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                    opacity: isSubmitting ? 0.6 : 1
                                }}
                            >
                                {__('取消', 'frontend-order-system')}
                            </button>
                            
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                style={{
                                    flex: 2,
                                    padding: '12px 20px',
                                    background: isSubmitting ? '#ccc' : '#0073aa',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '4px',
                                    fontSize: '1rem',
                                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                    opacity: isSubmitting ? 0.6 : 1
                                }}
                            >
                                {isSubmitting ? (
                                    <>⏳ {__('處理中...', 'frontend-order-system')}</>
                                ) : (
                                    <>🚀 {__('確認並結帳', 'frontend-order-system')}</>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
                
                {/* Privacy Notice */}
                <div style={{
                    padding: '16px 20px',
                    background: '#f8f9fa',
                    borderTop: '1px solid #e1e1e1',
                    fontSize: '0.8rem',
                    color: '#666',
                    textAlign: 'center'
                }}>
                    🔒 {__('您的個人信息將受到保護，僅用於處理此訂單', 'frontend-order-system')}
                </div>
            </div>
        </div>
    );
};

export default GuestCheckoutModal;
